import React, { FC, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { IAttachmentDTO } from 'src/api/dtos';
import { DeleteIcon, DownloadIcon, FileIcon } from 'src/shared/components';
import { formatFileSize } from 'src/shared/utils';

import { FilePreviewModal } from './FilePreviewModal';

interface FileChipProps {
  file?: File;
  attachment?: IAttachmentDTO;
  loading?: boolean;
  isDownloading?: boolean;
  isDeleting?: boolean;
  canPreview?: boolean;
  fileName?: string;
  fileSizeInBytes?: number;
  hasThumbnail?: boolean;
  disableDelete?: boolean;
  disableDownload?: boolean;
  onDeleteFile?: (file?: File) => void;
  onDownloadFile?: () => void;
}

const FileThumbnail: FC<{ file?: File; onClick?: () => void }> = ({ file, onClick }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!file) return;

    const type = file.type;

    // IMAGE PREVIEW
    if (type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setSrc(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  return (
    <Box
      sx={{
        width: 40,
        height: 38,
        borderRadius: 1.5,
        overflow: 'hidden',
        flexShrink: 0,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      {src ? (
        <img src={src} alt={file?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <FileIcon size={40} />
      )}
    </Box>
  );
};

export const FileChip: FC<FileChipProps> = ({
  file,
  loading,
  attachment,
  canPreview,
  fileName,
  fileSizeInBytes,
  hasThumbnail = true,
  disableDelete,
  disableDownload,
  onDeleteFile,
  onDownloadFile,
}) => {
  const [open, setOpen] = useState<boolean>(false);

  if (loading) {
    return (
      <Stack>
        <Skeleton variant="rectangular" width="100%" height={48} />
      </Stack>
    );
  }

  return (
    <>
      <Chip
        variant="outlined"
        label={
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" flex={1} flexGrow={1}>
            <Stack direction="row" alignItems="center" gap={1} justifyContent="flex-start" sx={{ minWidth: 0 }}>
              {hasThumbnail && <FileThumbnail file={file} onClick={canPreview ? () => setOpen(true) : undefined} />}
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
              >
                {fileName || (file ? file?.name : attachment?.fileName)}
              </Typography>
              <Typography
                variant="caption"
                color={(theme) => theme.palette.text.secondary}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {formatFileSize(fileSizeInBytes ?? (file ? file?.size : attachment?.fileSizeInBytes ?? 0))}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              {onDownloadFile && (
                <IconButton disabled={disableDownload} onClick={onDownloadFile} sx={{ ml: 1 }} color="primary">
                  <DownloadIcon size={20} />
                </IconButton>
              )}
              {onDeleteFile && (
                <IconButton disabled={disableDelete} onClick={() => onDeleteFile(file)} color="error" sx={{ ml: 1 }}>
                  <DeleteIcon size={20} />
                </IconButton>
              )}
            </Stack>
          </Stack>
        }
        sx={{
          height: 48,
          width: '100%',
          borderRadius: 2,
          '& .MuiChip-label': {
            width: '100%',
            padding: 1,
            display: 'flex',
          },
        }}
      />
      {canPreview && file && <FilePreviewModal file={file} open={open} onClose={() => setOpen(false)} />}
    </>
  );
};
